// Save / un-save a game to the browser-local Saved list (#120).
//
// Extracted from GameTable so the click-isolation behavior below can be tested
// directly — that behavior IS an acceptance criterion ("does not conflict with
// opening the game"), and it is exactly the kind of thing that regresses
// silently when someone edits the row markup later.
//
// Placement note: this renders inside the Date cell rather than a new leading
// column. Every row type in GameTable (team rows, ladder rows, archived rows,
// skeleton rows) spells out its own cells, so a new column would have to be
// threaded through all of them plus the spacer's colSpan arithmetic — a lot of
// alignment risk for one control.
//
// Glyph note: deliberately NOT a star. SharpStar already renders an amber ★ on
// this table for the Pinnacle sharp anchor. Saved state is carried by the
// glyph's FILL rather than by hue, so it competes with neither that amber nor
// the blue selection rails.

import { Bookmark } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useSavedGames, gameMarkId, isRowSaved, type SavableGame } from '@/stores/savedGames';
import { useAuthStore } from '@/stores/authStore';

// Takes the saved list's own identity shape rather than a GameRow, so the
// Spread/Total board's matchup rows (#1130) mount the same control.
export function SaveGameToggle({ game }: { game: SavableGame }) {
  const savable = gameMarkId(game.key) != null;
  // Subscribed, not read once: the control must re-render the moment the signed-in
  // user changes, so a list belonging to the previous account cannot survive an
  // account switch on screen even for a frame.
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const saved = useSavedGames((s) => isRowSaved(game.key, s, viewerId));
  const toggle = useSavedGames((s) => s.toggle);

  // Playoff-series rows carry a fabricated "9999-<round>-<yy>" date and so have
  // no canonical game key — nothing to save.
  if (!savable) return null;

  const matchup = `${game.awayCode} @ ${game.homeCode}`;
  return (
    <button
      type="button"
      onClick={(e) => {
        // Both are load-bearing. The whole <tr> carries an onClick that expands
        // the game's ladders, and on most sports this button sits beside a
        // <Link> to game detail inside that row — a save click must trigger
        // neither. (On ATP/WTA that neighbour is now a plain span, #631, but
        // the preventDefault stays: it is guarding the general case, and the
        // row's expand onClick is present on every sport.)
        e.stopPropagation();
        e.preventDefault();
        toggle(game, viewerId);
      }}
      aria-pressed={saved}
      aria-label={saved ? `Remove ${matchup} from saved games` : `Save ${matchup}`}
      title={saved ? 'Remove from Saved games' : 'Save this game'}
      className={cn(
        'shrink-0 rounded p-0.5 transition-colors',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
        saved ? 'text-foreground' : 'text-muted-foreground/40 hover:text-foreground',
      )}
    >
      <Bookmark size={12} className={saved ? 'fill-current' : undefined} />
    </button>
  );
}
